/**
 * Pestaña "Adjuntos" a nivel de proyecto.
 * Igual que la de Ficha FFE pero ligada al proyecto (para proyectos creados
 * manualmente sin cotización). Permite subir cualquier archivo con descripción
 * obligatoria, editar nombre/descripción, ver inline y eliminar.
 * Archivos van al bucket MinIO "adjuntos-fichaffe" (prefijo proyecto_{id}/).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import client from '../../../services/graphqlClient';
import { UPLOAD_URL } from '../../../config';
import '../../Documentos/AdjuntosFfeTab.css';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatBytes = (b) => {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const AdjuntosProyectoTab = ({ proyectoId }) => {
  const { success, error: showError } = useNotification();
  const [adjuntos, setAdjuntos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const fileInputRef = useRef(null);
  const [modalUpload, setModalUpload] = useState({ visible: false, file: null, descripcion: '' });
  const [editando, setEditando] = useState(null);

  const cargar = async () => {
    if (!proyectoId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await client.query(
        `query($pid: Int!) {
          adjuntosProyecto(proyecto_id: $pid) {
            id proyecto_id nombre_archivo descripcion key_minio mime_type tamano_bytes created_at updated_at
          }
        }`,
        { pid: proyectoId }
      );
      setAdjuntos(data.adjuntosProyecto || []);
    } catch (e) {
      showError('Error cargando adjuntos: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [proyectoId]);

  const onFileSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setModalUpload({ visible: true, file: f, descripcion: '' });
    e.target.value = '';
  };

  const cancelarSubida = () => setModalUpload({ visible: false, file: null, descripcion: '' });

  const confirmarSubida = async () => {
    const { file, descripcion } = modalUpload;
    if (!file) return;
    if (!descripcion.trim()) {
      showError('La descripción es obligatoria');
      return;
    }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('proyecto_id', proyectoId);
      fd.append('descripcion', descripcion.trim());
      const token = localStorage.getItem('tuvi_token');
      const r = await fetch(`${UPLOAD_URL}/proyecto-adjunto`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const json = await r.json();
      if (!json.success) throw new Error(json.message || 'Error subiendo archivo');
      success('Adjunto subido');
      setModalUpload({ visible: false, file: null, descripcion: '' });
      cargar();
    } catch (e) {
      showError('Error: ' + e.message);
    } finally {
      setSubiendo(false);
    }
  };

  const eliminar = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.nombre_archivo}"? Esta acción no se puede deshacer.`)) return;
    try {
      const r = await client.mutate(
        `mutation($id: Int!) { eliminarAdjuntoProyecto(id: $id) { success message } }`,
        { id: a.id }
      );
      if (r.eliminarAdjuntoProyecto.success) {
        success('Adjunto eliminado');
        cargar();
      } else {
        showError(r.eliminarAdjuntoProyecto.message);
      }
    } catch (e) {
      showError('Error: ' + e.message);
    }
  };

  const guardarEdicion = async () => {
    if (!editando) return;
    if (!editando.nombre_archivo.trim()) {
      showError('El nombre no puede estar vacío');
      return;
    }
    try {
      await client.mutate(
        `mutation($id: Int!, $d: String!) { actualizarDescripcionAdjuntoProyecto(id: $id, descripcion: $d) { success message } }`,
        { id: editando.id, d: editando.descripcion || '' }
      );
      await client.mutate(
        `mutation($id: Int!, $n: String!) { renombrarAdjuntoProyecto(id: $id, nombre_archivo: $n) { success message } }`,
        { id: editando.id, n: editando.nombre_archivo.trim() }
      );
      success('Adjunto actualizado');
      setEditando(null);
      cargar();
    } catch (e) {
      showError('Error: ' + e.message);
    }
  };

  const urlInline = (a) => `${UPLOAD_URL}/proyecto-adjunto/${encodeURIComponent(a.key_minio)}?inline=1`;
  const urlDownload = (a) => `${UPLOAD_URL}/proyecto-adjunto/${encodeURIComponent(a.key_minio)}`;

  const iconoTipo = (mime, nombre) => {
    const ext = (nombre || '').split('.').pop().toLowerCase();
    if ((mime || '').startsWith('image/')) return '🖼';
    if (mime === 'application/pdf' || ext === 'pdf') return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
    if (['mp4', 'mov', 'webm'].includes(ext)) return '🎬';
    if (['zip', 'rar', '7z'].includes(ext)) return '🗜';
    return '📎';
  };

  const adjuntosOrdenados = useMemo(
    () => [...adjuntos].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [adjuntos]
  );

  if (loading) return <div className="adj-loading">Cargando adjuntos…</div>;

  return (
    <div className="adj-root">
      <div className="adj-header">
        <div>
          <h3>Adjuntos</h3>
          <p className="adj-sub">{adjuntos.length} archivo(s) asociado(s) al proyecto</p>
        </div>
        <button className="adj-btn adj-btn-primary" onClick={() => fileInputRef.current?.click()}>
          + Subir adjunto
        </button>
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={onFileSelected} />
      </div>

      {adjuntos.length === 0 ? (
        <div className="adj-empty">
          <p>No hay archivos adjuntos todavía.</p>
          <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
            Sube cualquier archivo (PDF, imagen, doc, hoja de cálculo, etc.) relacionado con este proyecto.
          </p>
        </div>
      ) : (
        <div className="adj-list">
          {adjuntosOrdenados.map(a => (
            <div key={a.id} className="adj-card">
              <div className="adj-icon">{iconoTipo(a.mime_type, a.nombre_archivo)}</div>
              <div className="adj-body">
                <div className="adj-titulo-row">
                  <div className="adj-titulo">{a.nombre_archivo}</div>
                  <span className="adj-fecha">{formatDate(a.created_at)}</span>
                </div>
                {a.descripcion && <div className="adj-descripcion">{a.descripcion}</div>}
                <div className="adj-meta">
                  {a.mime_type && <span>{a.mime_type}</span>}
                  {a.tamano_bytes > 0 && <span>· {formatBytes(a.tamano_bytes)}</span>}
                </div>
              </div>
              <div className="adj-acciones">
                <button
                  className="adj-btn-icon adj-btn-ver"
                  onClick={() => window.open(urlInline(a), '_blank', 'noopener,noreferrer')}
                  title="Ver"
                >👁</button>
                <a
                  className="adj-btn-icon adj-btn-dl"
                  href={urlDownload(a)}
                  download={a.nombre_archivo}
                  title="Descargar"
                >⬇</a>
                <button
                  className="adj-btn-icon adj-btn-edit"
                  onClick={() => setEditando({ id: a.id, nombre_archivo: a.nombre_archivo, descripcion: a.descripcion || '' })}
                  title="Editar"
                >✎</button>
                <button
                  className="adj-btn-icon adj-btn-del"
                  onClick={() => eliminar(a)}
                  title="Eliminar"
                >🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: subir adjunto (pide descripcion) */}
      {modalUpload.visible && (
        <div className="adj-modal-overlay" onClick={cancelarSubida}>
          <div className="adj-modal" onClick={e => e.stopPropagation()}>
            <h3>Subir adjunto</h3>
            <div className="adj-modal-file">
              <span className="adj-modal-file-icon">{iconoTipo(modalUpload.file?.type, modalUpload.file?.name)}</span>
              <div>
                <div className="adj-modal-file-name">{modalUpload.file?.name}</div>
                <div className="adj-modal-file-meta">{formatBytes(modalUpload.file?.size)}</div>
              </div>
            </div>
            <label className="adj-modal-label">Descripción <span style={{ color: '#dc2626' }}>*</span></label>
            <textarea
              className="adj-modal-input"
              value={modalUpload.descripcion}
              onChange={e => setModalUpload(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Describe para qué sirve este adjunto..."
              rows={3}
              autoFocus
            />
            <div className="adj-modal-actions">
              <button className="adj-btn adj-btn-secondary" onClick={cancelarSubida} disabled={subiendo}>Cancelar</button>
              <button className="adj-btn adj-btn-primary" onClick={confirmarSubida} disabled={subiendo || !modalUpload.descripcion.trim()}>
                {subiendo ? 'Subiendo…' : 'Subir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: editar nombre + descripcion */}
      {editando && (
        <div className="adj-modal-overlay" onClick={() => setEditando(null)}>
          <div className="adj-modal" onClick={e => e.stopPropagation()}>
            <h3>Editar adjunto</h3>
            <label className="adj-modal-label">Nombre del archivo</label>
            <input
              className="adj-modal-input"
              type="text"
              value={editando.nombre_archivo}
              onChange={e => setEditando(prev => ({ ...prev, nombre_archivo: e.target.value }))}
            />
            <label className="adj-modal-label" style={{ marginTop: 12 }}>Descripción</label>
            <textarea
              className="adj-modal-input"
              value={editando.descripcion}
              onChange={e => setEditando(prev => ({ ...prev, descripcion: e.target.value }))}
              rows={3}
            />
            <div className="adj-modal-actions">
              <button className="adj-btn adj-btn-secondary" onClick={() => setEditando(null)}>Cancelar</button>
              <button className="adj-btn adj-btn-primary" onClick={guardarEdicion}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdjuntosProyectoTab;
